// Extracted verbatim from RelationshipValueEditorMolecule's private
// `RelatedSphereChip` so the reference-property value editor
// (`SphereMultiSelectValueMolecule`) resolves a stored sphere id to a name the
// same way the relationship lane already does — one resolution rule, not two
// that can disagree about what an unresolvable id reads as.
import React from 'react';
import { useTranslation } from 'localization';
import { isFilterVariable } from '@center-platform/spheres/filters';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import { useSphereInfo } from '../../../hooks/useSphereInfo';

export interface SphereRefChipMoleculeProps {
  /** A sphere id, or a `@`-variable token (`@me`, `@root`, …). */
  sphereId: string;
  readOnly?: boolean;
  onRemove?: () => void;
  className?: string;
}

/**
 * Resolves a stored sphere reference to a display chip. A `@`-variable token
 * renders literally — never resolved client-side, because resolving it at
 * authoring time would silently rewrite the condition's meaning per viewer.
 * While the lookup is in flight the id shows rather than flashing the
 * unavailable copy.
 */
const SphereRefChipMolecule: React.FC<SphereRefChipMoleculeProps> = ({
  sphereId,
  readOnly,
  onRemove,
  className = '',
}) => {
  const { t } = useTranslation();
  const isVariable = isFilterVariable(sphereId);
  const { info, loading } = useSphereInfo(isVariable ? undefined : sphereId);

  // Some callers populate `label` at the top level (basic-sphere-info shape from the picker
  // and the roster route), others only populate `properties.name` (full-sphere shape from
  // getSphereById). Try both before falling back to the "unavailable" copy — otherwise a chip
  // for a sphere that IS resolved renders as Unavailable just because the cache returned the
  // wrong shape (EAINT-877 pin chip regression).
  // `useSphereInfo`'s local `SphereInfo` type declares a flat `{ label, properties }` shape, but
  // what `globalSphereCache` actually returns is wrapped: `info = { sphere: { label, properties } }`.
  // The hook casts the cache result and hides the mismatch. Read the wrapped shape here (that's
  // where the data actually lives), then fall back to the flat one in case a caller populated the
  // hook's declared type manually, then to the "unavailable" copy.
  const wrappedSphere = (info as unknown as { sphere?: { label?: string; properties?: { name?: string } } })?.sphere;
  const resolvedName =
    wrappedSphere?.properties?.name ?? wrappedSphere?.label ?? info?.properties?.name ?? info?.label;
  const label =
    isVariable || loading
      ? sphereId
      : (resolvedName ?? t('context.editor.relationshipFilters.sphereUnavailable'));

  return (
    <ChipAtom
      label={label}
      icon={isVariable ? 'pi pi-at' : undefined}
      removable={!readOnly && !!onRemove}
      onRemove={() => {
        onRemove?.();
        return false;
      }}
      className={className}
    />
  );
};

export default SphereRefChipMolecule;
